import { useState } from "react";

import Alert from "client/common/components/content/Alert";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { getFailureDetailsByField } from "client/controlledSubstances/utils";

import { useClientUserQuery, useUpdateProfileMutation } from "../../hooks";
import { AccountComplianceContext } from "../../types";
import TextInput from "../../../common/components/inputs/TextInput";

export type AccountUpdateNameDrawerProps = {
  compliance?: AccountComplianceContext;
};

const AccountUpdateNameDrawer = ({ compliance }: AccountUpdateNameDrawerProps) => {
  const { data: clientUser, isLoading, error } = useClientUserQuery();
  const { close } = useCurrentDrawer();
  const [firstName, setFirstName] = useState(clientUser?.first_name || "");
  const [lastName, setLastName] = useState(clientUser?.last_name || "");

  const {
    mutate: updateProfile,
    isPending: isUpdating,
    error: updateError,
  } = useUpdateProfileMutation({ onSuccess: close });

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!clientUser || error) {
    return <DrawerLayout.FetchError error={error} entityName="account" />;
  }
  const title = "Update name";
  const complianceDetails = getFailureDetailsByField(compliance?.failures ?? []);

  return (
    <DrawerLayout>
      <DrawerLayout.Header title={title} onClose={close} />
      <DrawerLayout.Form
        submitText="Update name"
        canSubmit={!isLoading && !isUpdating}
        onSubmit={() => updateProfile({ first_name: firstName, last_name: lastName })}
        error={updateError}
      >
        {!!compliance && (
          <DrawerLayout.Alerts>
            <Alert variant="danger">{compliance.message}</Alert>
          </DrawerLayout.Alerts>
        )}
        <DrawerLayout.Form.Group>
          <TextInput
            value={firstName}
            label="First Name"
            onChange={setFirstName}
            errors={[
              ...(updateError?.errors.first_name ?? []),
              ...(complianceDetails.first_name ?? []),
            ]}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group>
          <TextInput
            value={lastName}
            label="Last Name"
            onChange={setLastName}
            errors={[
              ...(updateError?.errors.last_name ?? []),
              ...(complianceDetails.last_name ?? []),
            ]}
          />
        </DrawerLayout.Form.Group>
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default AccountUpdateNameDrawer;
